import {observer} from 'mobx-react';
import React from 'react';
import {
  Link,
  PageHeading,
  PromiseMessage,
  PromiseRetry,
  Redirect,
  TimeZoneSelect
} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {UserViewRender} from '../view-render/UserViewRender';
import {UserViewSceneStore} from './UserViewSceneStore';

function* setTimeZoneControls(sceneStore: UserViewSceneStore): ReactNodeGenerator {
  if (sceneStore.mode !== 'set-timezone')
    return;

  const timeZonesState = sceneStore.timeZonesState;
  yield (<PromiseMessage key="timezone-loader"
                         state={timeZonesState} label="Loading time zones" align="center"/>);

  yield (<PromiseRetry key="timezone-retry"
                       state={timeZonesState} retry={sceneStore.loadTimeZones} align="center"/>);

  if (timeZonesState?.isResolved !== true)
    return;

  const store = sceneStore.setTimeZone!;
  const timeZone = store.timeZone;
  const state = store.save;

  yield (
    <div className="row" key="set-time-zone-controls">
      <div className="col-md-4 col-xs-6">
        <div className={timeZone.formGroupClassName}>
          <label>Time zone</label>
          <TimeZoneSelect options={store.options}
                          value={timeZone.value}
                          onChange={timeZone.setValue}
                          onBlur={timeZone.markTouched}/>
          <div className="invalid-text">Enter time zone</div>
        </div>
      </div>
      <div className="col-auto ml-auto">
        <button type="button" className="btn btn-primary"
                onClick={sceneStore.completeSetTimeZone}>
          Update
        </button>

        <button type="button" className="btn btn-primary"
                onClick={sceneStore.cancel}>
          Cancel
        </button>
      </div>
    </div>);

  yield (<PromiseMessage key="set-timezone-state"
                         label="Updating time zone" state={state} align="right"/>);

}

function* setPasswordControls(sceneStore: UserViewSceneStore): ReactNodeGenerator {
  if (sceneStore.mode !== 'set-password')
    return;

  const store = sceneStore.setPassword!;
  const password = store.password;
  const state = store.save;

  yield (
    <div className="row" key="set-password-controls">
      <div className="col-md-4 col-xs-6">
        <div className={password.formGroupClassName}>
          <label>New password </label>
          <input type="text" className="form-control"
                 value={password.value}
                 onChange={password.onChangeHandler}
                 onBlur={password.markTouched}/>
          <div className="invalid-text">Enter new password</div>
        </div>
      </div>
      <div className="col-auto ml-auto">
        <button type="button" className="btn btn-primary"
                onClick={sceneStore.completeSetPassword}>
          Update
        </button>

        <button type="button" className="btn btn-primary"
                onClick={sceneStore.cancel}>
          Cancel
        </button>
      </div>
    </div>);

  yield (<PromiseMessage key="set-password-state"
                         label="Updating password" state={state} align="right"/>);
}

function* deleteControls(sceneStore: UserViewSceneStore): ReactNodeGenerator {
  if (sceneStore.mode !== 'delete')
    return;

  const store = sceneStore.deleteUser!;
  const state = store.save;
  const isFailed = state?.isRejected === true;

  yield (<PromiseMessage key="delete-user-state"
                         label="Deleting user" state={state} align="right"/>);
  if (isFailed)
    yield (
      <div className="row" key="cancel-delete">
        <div className="col-auto ml-auto">
          <button type="button" className="btn btn-primary"
                  onClick={sceneStore.cancel}>
            Cancel
          </button>
        </div>
      </div>);
}

function* renderer(store: UserViewSceneStore): ReactNodeGenerator {
  const state = store.userState;

  if (state?.isPending === true || state?.isRejected === true) {
    yield (<PageHeading key="heading"> User {store.userId} </PageHeading>);

    yield (<PromiseMessage key="user-loader"
                           label="Loading user" state={state} align="center"/>);

    yield (<PromiseRetry key="user-retry"
                         state={state} retry={store.loadUser} align="center"/>);
  }

  if (state?.isResolved !== true)
    return;

  const user = state.value;

  yield <PageHeading key="heading"> User {user.Email} </PageHeading>;

  yield (
    <div className="row" key="main">
      <div className="col">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> User details</div>
              <div className="col-auto no-wrap">
                <Link to={routes.userEdit(user.Id)}
                      className="btn btn-primary">
                  <span className="fas fa-pencil-alt"/> Edit
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            <UserViewRender user={user} adminView/>

            {
              store.mode === 'view' &&
              <div className="row">
                <div className="col-auto ml-auto">
                  <button type="button" className="btn btn-primary"
                          onClick={store.beginSetTimeZone}>
                    <span className="fas fa-user-clock"/> Update time zone
                  </button>
                  <button type="button" className="btn btn-primary"
                          onClick={store.beginSetPassword}>
                    <span className="fas fa-key"/> Update password
                  </button>

                  <button type="button" className="btn btn-danger"
                          onClick={store.confirmThenDelete}>
                    <span className="fas fa-trash-alt"/> Delete user
                  </button>
                </div>
              </div>
            }

            {Array.from(setTimeZoneControls(store))}

            {Array.from(setPasswordControls(store))}

            {Array.from(deleteControls(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

@observer
export class UserViewScene extends React.Component<{ id: number }> {
  readonly store: UserViewSceneStore;

  constructor(props: Readonly<{ id: number }>) {
    super(props);
    this.store = new UserViewSceneStore(props.id);
  }

  componentDidMount() {
    this.store.loadUser();
  }

  render() {
    const store = this.store;

    if (store.redirectTo !== null)
      return (<Redirect to={store.redirectTo}/>);

    return Array.from(renderer(store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
